import { useEditorState } from '@tiptap/react';
import { useEffect, useState } from 'react';

import { FEATURES, useFeature } from '@/features/feature-flags';
import { collectMentions, useComposerEditorOf } from '@/features/mention';

import { mentionedPersonIds } from '../mentions';
import { usePeopleWikiQuery } from '../people-wiki.query';

import { addressesToLookUp } from './connection-actions';
import { useLittlebirdAccountsQuery } from './connections.query';
import { sendToTaggedPeople } from './send-to-tagged-people';
import {
  CARD_SELECTOR,
  cardForTaggedPeople,
  type Card,
  type CardStatus,
} from './tagged-people';
import { dismissCard, isCardDismissed } from './tagged-people.store';

// How long a card says that the action went through, before it goes
const DONE_VISIBLE_MS = 1500;

type Working = { card: Card; status: Exclude<CardStatus, 'idle'> };

/** The card to show above the composer for the people tagged in the draft, and what its buttons do. While an action
 * runs, and for a moment after it, the card stays as the user saw it, because the people it names stop belonging to it
 * as soon as the action goes through. */
export const useTaggedPeopleCards = (draftId: string) => {
  const isEnabled = useFeature()(FEATURES.PEOPLE_WIKI);
  const { data: wikiPeople = [] } = usePeopleWikiQuery({ enabled: isEnabled });
  const editor = useComposerEditorOf(draftId);
  const taggedIds =
    useEditorState({
      editor: editor ?? null,
      selector: () =>
        editor ? mentionedPersonIds(collectMentions(editor)) : [],
    }) ?? [];
  const [working, setWorking] = useState<Working | null>(null);

  const taggedPeople = isEnabled
    ? wikiPeople.filter(
        ({ id }) => taggedIds.includes(id) && !isCardDismissed(draftId, id)
      )
    : [];
  // Only the tagged people's addresses are looked up, and the answer for the people before stays while the new ones are
  const { data: answer = null } = useLittlebirdAccountsQuery(
    [...new Set(taggedPeople.flatMap(addressesToLookUp))],
    { keepPrevious: true }
  );
  const card = working?.card ?? cardForTaggedPeople(taggedPeople, answer);

  useEffect(() => {
    if (working?.status !== 'done') return;
    const timer = window.setTimeout(() => setWorking(null), DONE_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [working]);

  const status: CardStatus = working?.status ?? 'idle';

  const run = async () => {
    if (!card || working) return;
    // Set before the question of an invite opens, so that a second click cannot open another
    setWorking({ card, status: 'sending' });
    const didSend = await sendToTaggedPeople(card);
    setWorking(didSend ? { card, status: 'done' } : null);
    // The button that the user used is gone once the card says that it went through, unless they moved on meanwhile
    const focused = document.activeElement;
    if (
      didSend &&
      (focused === document.body || focused?.closest(CARD_SELECTOR))
    ) {
      editor?.commands.focus();
    }
  };

  return {
    card,
    status,
    run,
    dismiss: () => {
      if (!card) return;
      dismissCard(
        draftId,
        card.people.map(({ person }) => person.id)
      );
      editor?.commands.focus();
    },
  };
};
